import React, { useEffect } from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import ReactDOM from 'react-dom';
import { Button } from '../Button';
import { Close } from '../Close';
import { Heading } from '../Heading';
import moduleStyles from '@dtsl/sidemodal/dist/index.module.css';
function getHeaderVariantClassName(headerVariant) {
  if (headerVariant === 'finished') {
    return moduleStyles['sib-side-modal__header--white'];
  }
  return '';
}
export const Sidemodal = props => {
  const {
    show = false,
    onHide = () => {},
    title = '',
    identifier = '',
    headerVariant = 'ongoing',
    content = () => {},
    primaryAction = null,
    secondaryAction = null,
    backdrop = true,
    isFocusInsideBody = false,
    className,
    headerClassName,
    bodyClassName,
    footerClassName
  } = props;
  const smRef = React.useRef();
  const [showDialog, setShowDialog] = React.useState(show);
  const onAnimationEnd = () => {
    if (!show) ReactDOM.flushSync(() => {
      setShowDialog(false);
    });
  };
  useEffect(() => {
    show && setShowDialog(show);
    const handleEscKey = event => {
      if (event.keyCode === 27) {
        show && onHide();
      }
    };
    if (backdrop) {
      if (show) {
        document.body.classList.add(moduleStyles['sib-side-modal-open']);
      } else {
        document.body.classList.remove(moduleStyles['sib-side-modal-open']);
      }
    }
    if (show && smRef.current) {
      const focusableItems = smRef.current?.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
      focusableItems[0]?.focus({
        preventScroll: true
      });
    }
    document.addEventListener('keydown', handleEscKey, false);
    return () => {
      document.removeEventListener('keydown', handleEscKey, false);
      if (document.body.classList.contains(moduleStyles['sib-side-modal-open'])) {
        document.body.classList.remove(moduleStyles['sib-side-modal-open']);
      }
    };
  }, [show]);
  const headerVariantClassName = getHeaderVariantClassName(headerVariant);
  const contentHtml = content();
  const backdropClasses = classNames({
    [moduleStyles['sib-backdrop']]: true,
    [moduleStyles['in']]: show
  });
  const sideModalClasses = classNames({
    [moduleStyles['sib-side-modal']]: true,
    [moduleStyles['sib-side-modal__toggled']]: show,
    [className]: !!className
  });
  const sideModalHeaderClasses = classNames({
    [moduleStyles['sib-side-modal__header']]: true,
    [headerVariantClassName]: true,
    [headerClassName]: !!headerClassName
  });
  const sideModalBodyClasses = classNames({
    [moduleStyles['body__data']]: true,
    [bodyClassName]: !!bodyClassName
  });
  const sideModalFooterClasses = classNames({
    [moduleStyles['content__footer']]: true,
    [footerClassName]: !!footerClassName
  });
  return showDialog && /*#__PURE__*/ReactDOM.createPortal(/*#__PURE__*/React.createElement(React.Fragment, null, backdrop ? /*#__PURE__*/React.createElement("div", {
    className: backdropClasses,
    onAnimationEnd: onAnimationEnd,
    style: {
      animation: `${show ? 'var(--sib-transition_sidemodal-backdrop-open, sidemodal-backdrop-open 350ms ease-in-out)' : 'var(--sib-transition_sidemodal-backdrop-close, sidemodal-backdrop-close 350ms ease-in-out)'}`
    }
  }) : null, /*#__PURE__*/React.createElement("div", {
    ref: !isFocusInsideBody ? smRef : null,
    role: "dialog",
    "aria-label": title,
    "aria-modal": true,
    id: identifier,
    className: sideModalClasses,
    onAnimationEnd: onAnimationEnd,
    style: {
      animation: `${show ? 'var(--sib-transition_slide-sidemodal-to-left, slide-sidemodal-to-left 550ms ease-in-out)' : 'var(--sib-transition_slide-sidemodal-to-right, 550ms slide-sidemodal-to-right ease-in-out)'}`
    }
  }, /*#__PURE__*/React.createElement("div", {
    className: sideModalHeaderClasses
  }, /*#__PURE__*/React.createElement(Heading, {
    className: `${moduleStyles['sib-side-modal__header--content']}`,
    fontFamily: "primary"
  }, title), /*#__PURE__*/React.createElement("span", null, /*#__PURE__*/React.createElement(Close, {
    onClick: onHide,
    className: "sib-cross-button"
  }))), /*#__PURE__*/React.createElement("div", {
    className: moduleStyles['sib-side-modal__body'],
    tabIndex: "0",
    role: "document",
    ref: isFocusInsideBody ? smRef : null
  }, /*#__PURE__*/React.createElement("div", {
    className: sideModalBodyClasses
  }, contentHtml)), /*#__PURE__*/React.createElement("footer", {
    className: moduleStyles['sib-side-modal__footer']
  }, /*#__PURE__*/React.createElement("div", {
    className: sideModalFooterClasses
  }, /*#__PURE__*/React.createElement("div", {
    className: `${moduleStyles['left-content__footer']}`
  }, secondaryAction ? /*#__PURE__*/React.createElement(Button, secondaryAction) : null), /*#__PURE__*/React.createElement("div", {
    className: `${moduleStyles['right-content__footer']}`
  }, primaryAction ? /*#__PURE__*/React.createElement(Button, primaryAction) : null))))), document.body);
};
Sidemodal.propTypes = {
  show: PropTypes.bool,
  onHide: PropTypes.func,
  backdrop: PropTypes.bool,
  title: PropTypes.string,
  content: PropTypes.func,
  className: PropTypes.string,
  headerClassName: PropTypes.string,
  bodyClassName: PropTypes.string,
  footerClassName: PropTypes.string,
  secondaryAction: PropTypes.shape({
    variant: PropTypes.oneOf(['primary', 'secondary', 'tertiary', 'destructive']),
    label: PropTypes.string,
    iconAlone: PropTypes.bool,
    icon: PropTypes.node,
    ellipsis: PropTypes.bool,
    disabled: PropTypes.bool,
    tooltip: PropTypes.bool,
    tooltipValue: PropTypes.object,
    dropdownOpen: PropTypes.bool,
    showArrowIcon: PropTypes.bool,
    className: PropTypes.string
  }),
  primaryAction: PropTypes.shape({
    variant: PropTypes.oneOf(['primary', 'secondary', 'tertiary', 'destructive']),
    label: PropTypes.string,
    iconAlone: PropTypes.bool,
    icon: PropTypes.node,
    ellipsis: PropTypes.bool,
    disabled: PropTypes.bool,
    tooltip: PropTypes.bool,
    tooltipValue: PropTypes.object,
    dropdownOpen: PropTypes.bool,
    showArrowIcon: PropTypes.bool,
    className: PropTypes.string
  }),
  headerVariant: PropTypes.string,
  identifier: PropTypes.string,
  isFocusInsideBody: PropTypes.bool
};
export default Sidemodal;